import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { formatCurrency, formatDate } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CreditCard, Receipt, TrendUp } from "@phosphor-icons/react";

export default function Billing() {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState({ total_paid: 0, total_due: 0, invoice_count: 0 });
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);

  const load = () => {
    Promise.all([
      api.get("/invoices").then(({data}) => setInvoices(data)),
      api.get("/billing/summary").then(({data}) => setSummary(data)),
    ]).catch(() => toast.error("Failed to load billing")).finally(() => setLoading(false));
  };

  useEffect(load, []);

  const pay = async (id) => {
    setPaying(id);
    try {
      await api.post(`/invoices/${id}/pay`);
      toast.success("Payment recorded");
      load();
    } catch (e) { toast.error("Payment failed"); }
    finally { setPaying(null); }
  };

  return (
    <AppShell subtitle="§ BILLING" title="Invoices & payments">
      <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-black/10 mb-8">
        <div className="border-r border-b border-black/10 p-6 bg-[#0B1F3A] text-white" data-testid="billing-total-paid">
          <div className="fx-mono-tag flex items-center gap-2" style={{color:'#a3a3a3'}}><TrendUp size={12} /> TOTAL PAID</div>
          <div className="mt-3 text-4xl font-black tracking-tighter font-mono">{formatCurrency(summary.total_paid)}</div>
          <div className="mt-2 text-xs text-neutral-400">Lifetime</div>
        </div>
        <div className="border-r border-b border-black/10 p-6" data-testid="billing-total-due">
          <div className="fx-mono-tag flex items-center gap-2"><Receipt size={12} /> OUTSTANDING</div>
          <div className="mt-3 text-4xl font-black tracking-tighter font-mono text-[#FF3B30]">{formatCurrency(summary.total_due)}</div>
          <div className="mt-2 text-xs text-neutral-500">Across open invoices</div>
        </div>
        <div className="border-r border-b border-black/10 p-6" data-testid="billing-invoice-count">
          <div className="fx-mono-tag flex items-center gap-2"><CreditCard size={12} /> INVOICES</div>
          <div className="mt-3 text-4xl font-black tracking-tighter font-mono">{summary.invoice_count}</div>
          <div className="mt-2 text-xs text-neutral-500">Total issued</div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold tracking-tight">All invoices</h2>
        <div className="fx-mono-tag">{invoices.length} TOTAL</div>
      </div>

      {loading ? (
        <div className="border border-black/10 p-10 text-center text-neutral-500">Loading…</div>
      ) : invoices.length === 0 ? (
        <div className="border border-dashed border-black/20 p-16 text-center">
          <div className="fx-mono-tag mb-2">NO INVOICES YET</div>
          <p className="text-neutral-600 text-sm">Invoices are generated automatically once a project is approved.</p>
        </div>
      ) : (
        <div className="border border-black/10">
          <div className="grid grid-cols-12 border-b border-black/10 bg-[#F4F6F8] px-5 py-3 fx-mono-tag">
            <div className="col-span-3">INVOICE</div>
            <div className="col-span-4">PROJECT</div>
            <div className="col-span-2">ISSUED</div>
            <div className="col-span-1 text-right">AMOUNT</div>
            <div className="col-span-2 text-right">STATUS</div>
          </div>
          {invoices.map(inv => (
            <div key={inv.id} className="grid grid-cols-12 px-5 py-4 items-center border-b border-black/5 last:border-b-0" data-testid={`invoice-row-${inv.id}`}>
              <div className="col-span-3 font-mono text-sm font-semibold">{inv.number}</div>
              <div className="col-span-4 text-sm truncate">{inv.project_title}</div>
              <div className="col-span-2 text-xs text-neutral-500 font-mono">{formatDate(inv.issued_at)}</div>
              <div className="col-span-1 text-right font-mono font-semibold">{formatCurrency(inv.amount_usd, inv.currency)}</div>
              <div className="col-span-2 flex justify-end items-center gap-2">
                <span className="fx-badge" style={{
                  background: inv.status === "paid" ? "#E4F5EA" : "#FFF7D6",
                  color: inv.status === "paid" ? "#1E7C3B" : "#8A6A00",
                  borderColor: inv.status === "paid" ? "#34C759" : "#FFCC00",
                }}>{inv.status.toUpperCase()}</span>
                {inv.status !== "paid" && (
                  <Button size="sm" disabled={paying === inv.id} onClick={() => pay(inv.id)} className="fx-btn-accent rounded-md h-8 text-xs" data-testid={`pay-invoice-${inv.id}`}>
                    {paying === inv.id ? "Processing…" : "Pay now"}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}
